import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, ChevronRight, Clock, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Canvas de signature (identique à TerrainSignatures) ──────────────────────

function SignatureCanvas({ onValidate, onCancel, signaireNom, onChangeSignataire, devis }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    lastPos.current = getPos(e, canvas);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasDrawing(true);
  };
  const endDraw = () => { drawing.current = false; };

  const handleValidate = () => {
    if (!hasDrawing) { toast.error('Veuillez signer dans le cadre'); return; }
    onValidate(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <div className="flex flex-col h-full" style={{ background: '#F8FAFF' }}>
      <div className="px-6 py-5" style={{ background: 'white', borderBottom: '1px solid #E2E8F0' }}>
        <h2 className="text-xl font-bold" style={{ color: '#1e3a5f' }}>{devis?.numero}</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>{devis?.client_nom}</p>
      </div>

      <div className="flex-1 p-6 flex flex-col gap-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: '#374151' }}>Nom du signataire</label>
          <input
            value={signaireNom}
            onChange={e => onChangeSignataire(e.target.value)}
            placeholder="Nom Prénom"
            className="w-full px-4 py-3 rounded-2xl border-2 text-base outline-none"
            style={{ borderColor: '#E2E8F0', background: 'white' }}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold" style={{ color: '#374151' }}>Signature</label>
            <button onClick={() => {
              const ctx = canvasRef.current.getContext('2d');
              ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
              setHasDrawing(false);
            }} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-xl"
              style={{ background: '#F1F5F9', color: '#64748B' }}>
              <RotateCcw size={11} /> Effacer
            </button>
          </div>
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full rounded-2xl touch-none"
            style={{ border: '2px dashed #CBD5E1', background: 'white', cursor: 'crosshair' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          <p className="text-xs mt-2 text-center" style={{ color: '#9CA3AF' }}>
            Signez ci-dessus pour confirmer la commande
          </p>
        </div>
      </div>

      <div className="px-6 py-5 flex gap-3" style={{ borderTop: '1px solid #E2E8F0', background: 'white' }}>
        <button onClick={handleValidate}
          className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base text-white"
          style={{ background: '#16A34A' }}>
          <Check size={20} /> Confirmer la commande
        </button>
        <button onClick={onCancel}
          className="px-5 py-4 rounded-2xl border-2 font-semibold text-base"
          style={{ borderColor: '#E2E8F0', color: '#6B7280' }}>
          Retour
        </button>
      </div>
    </div>
  );
}

// ─── Carte devis ───────────────────────────────────────────────────────────────

function DevisCard({ devis, onSign }) {
  return (
    <div
      onClick={onSign}
      className="flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer active:scale-[0.98] transition-transform"
      style={{ borderColor: '#E2E8F0', background: 'white' }}
      data-testid={`devis-card-${devis.vente_id}`}
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: '#F5F3FF' }}>
        <ShoppingCart size={22} style={{ color: '#7C3AED' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold" style={{ color: '#1e3a5f', fontSize: '1rem' }}>{devis.numero}</p>
        <p className="text-sm font-semibold truncate" style={{ color: '#374151' }}>{devis.client_nom}</p>
        {devis.signature?.signataire_nom && (
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Signataire : {devis.signature.signataire_nom}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ background: '#F5F3FF', color: '#7C3AED' }}>
          <Clock size={10} className="inline mr-1" />À signer
        </span>
        <ChevronRight size={18} style={{ color: '#CBD5E1' }} />
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function TerrainCommandesVente() {
  const navigate = useNavigate();
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [signaireNom, setSignaireNom] = useState('');
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getDevisVenteEnAttenteSignature();
      setDevis(res.data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleValidate = async (dataUrl) => {
    if (!selected) return;
    setSigning(true);
    try {
      await api.signerVenteTerrain(selected.vente_id, {
        signataire_nom: signaireNom,
        signature_image_b64: dataUrl,
      });
      toast.success('Commande confirmée !');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  // Plein écran signature
  if (selected) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#F8FAFF' }}>
        {signing && (
          <div className="absolute inset-0 flex items-center justify-center z-50"
            style={{ background: 'rgba(255,255,255,0.85)' }}>
            <p className="text-lg font-bold" style={{ color: '#1e3a5f' }}>Confirmation en cours...</p>
          </div>
        )}
        <SignatureCanvas
          devis={selected}
          signaireNom={signaireNom}
          onChangeSignataire={setSignaireNom}
          onValidate={handleValidate}
          onCancel={() => setSelected(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F0F4FF' }}>
      <div className="flex items-center gap-4 px-6 py-5" style={{ background: 'white', borderBottom: '1px solid #E2E8F0' }}>
        <button onClick={() => navigate('/terrain')}
          className="p-2.5 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={20} style={{ color: '#374151' }} />
        </button>
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#1e3a5f' }}>Commandes de vente</h1>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {devis.length} devis{devis.length !== 1 ? '' : ''} en attente de signature
          </p>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p style={{ color: '#9CA3AF' }}>Chargement...</p>
          </div>
        ) : devis.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#F5F3FF' }}>
              <ShoppingCart size={28} style={{ color: '#7C3AED' }} />
            </div>
            <p className="text-base font-semibold" style={{ color: '#374151' }}>Aucun devis en attente</p>
            <p className="text-sm text-center" style={{ color: '#9CA3AF' }}>
              Les devis envoyés en signature tablette depuis le bureau apparaîtront ici
            </p>
          </div>
        ) : (
          devis.map(d => (
            <DevisCard
              key={d.vente_id}
              devis={d}
              onSign={() => { setSelected(d); setSignaireNom(d.signature?.signataire_nom || ''); }}
            />
          ))
        )}
      </div>

      <div className="px-6 py-4 text-center" style={{ borderTop: '1px solid #E2E8F0', background: 'white' }}>
        <button onClick={load} className="text-sm font-medium" style={{ color: '#7C3AED' }}>Actualiser</button>
      </div>
    </div>
  );
}
